// JOBE-0162: Organization roster — department-card grid.
//
// Rebuilt for the W3 wireframe (see
// docs/proposals/jobe-webui-workforce-navigation/wireframes.html
// #pane-org). Each department renders a card; the people inside the
// department render as compact rows inside that card.
//
// JOBE-0162.2: type/dept filtering happens upstream via applyFilters().
// JOBE-0162.3: humans show OWNS-N chip; agents show owned-by link;
//              both threaded with drillContext for back-button restore.
// JOBE-0162.4: empty groups are suppressed; OrganizationEmpty handles
//              the all-filtered-out case in the page.
//
// Preserved data-testids (for tests + e2e selectors):
//   organization-roster, org-dept-group-<dept>, org-row-<id>,
//   org-row-type-<id>, org-row-link-<id>, org-row-owns-<id>,
//   org-row-owned-by-<id>.

import React from 'react';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { withDrillContext } from '../lib/drillContext.js';
import { avatarHue } from '../agentAvatar.js';
import { countOwnedBy, lookupOwner } from './ownership.js';

const PHASE_TONE = {
  Available: 'ok',
  Degraded: 'warn',
  Disabled: 'bad',
};

function deptLabel(slug) {
  if (!slug) return '';
  return slug
    .split(/[-_\s]+/)
    .map((p) => (p.length === 0 ? '' : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ');
}

function deptInitials(slug) {
  if (!slug) return '·';
  const parts = slug.split(/[-_\s]+/).filter(Boolean);
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function initials(displayName) {
  if (!displayName) return '';
  const parts = displayName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

/**
 * Pure helper — filter rows by type, dept, ownerId (JOBE-0162.3),
 * and search query (JOBE-0162.4).
 * Exported for testability and reuse from OrganizationPage.
 *
 * The query is matched case-insensitively against `displayName`, `role`,
 * and `email` (concatenated; not fuzzy).
 */
export function applyFilters(rows, { typeFilter, deptFilter, ownerId, query }) {
  const needle = (query || '').trim().toLowerCase();
  return (rows || []).filter((r) => {
    if (typeFilter && typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (deptFilter && deptFilter !== 'all' && r.dept !== deptFilter) return false;
    if (ownerId && r.ownerId !== ownerId) return false;
    if (needle) {
      const hay = [r.displayName, r.role, r.email]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
}

/**
 * Group rows by dept slug. Returns Map(dept -> rows[]) ordered
 * alphabetically by dept slug — matches the prior public contract
 * the roster test asserts on.
 */
function groupByDept(rows) {
  const buckets = new Map();
  for (const r of rows) {
    const k = r.dept || '';
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k).push(r);
  }
  const ordered = new Map();
  for (const d of [...buckets.keys()].sort()) {
    ordered.set(d, buckets.get(d));
  }
  return ordered;
}

/**
 * Compute the department health badge from its rows. Empty → no badge.
 * Any Disabled agent → block; any Degraded → watch; else ok.
 */
function deptHealth(rows) {
  let hasBlock = false;
  let hasWatch = false;
  for (const r of rows) {
    if (r.type !== 'agent') continue;
    if (r.phase === 'Disabled') hasBlock = true;
    else if (r.phase === 'Degraded') hasWatch = true;
  }
  if (hasBlock) return { tone: 'block', label: 'Attention' };
  if (hasWatch) return { tone: 'watch', label: 'Degraded' };
  return { tone: 'ok', label: 'Healthy' };
}

function OrganizationRow({ row, allRows, drillContext, onOwnerChipClick }) {
  const isAgent = row.type === 'agent';
  const targetUrl = isAgent
    ? `/agent/${row.id}`
    : `/organization/people/${row.id}`;
  const href = withDrillContext(targetUrl, drillContext);
  const hue = avatarHue(row.id);
  const phaseTone = isAgent && row.phase ? PHASE_TONE[row.phase] : null;

  // JOBE-0162.3: humans get a "OWNS N" chip when N>0.
  const ownsCount = !isAgent ? countOwnedBy(allRows, row.id) : 0;
  const owner = isAgent && row.ownerId ? lookupOwner(allRows, row.ownerId) : null;

  return (
    <li
      className="org-row"
      data-testid={`org-row-${row.id}`}
      data-type={row.type}
    >
      <span
        className="org-row__avatar"
        aria-hidden="true"
        style={{ background: hue.bg, color: hue.fg }}
      >
        {initials(row.displayName)}
      </span>
      <span className="org-row__main">
        <span className="org-row__name">
          {row.displayName}
          {!isAgent && ownsCount > 0 ? (
            <span
              className="org-owns-chip-slot"
              data-testid={`org-row-owns-${row.id}`}
            >
              <PillChip
                label={`OWNS ${ownsCount}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onOwnerChipClick === 'function') {
                    onOwnerChipClick(row);
                  }
                }}
              />
            </span>
          ) : null}
          {isAgent && row.ownerId ? (
            owner ? (
              <a
                className="org-owner-link"
                data-testid={`org-row-owned-by-${row.id}`}
                href={withDrillContext(
                  `/organization/people/${owner.id}`,
                  drillContext,
                )}
                onClick={(e) => e.stopPropagation()}
              >
                owned by {owner.displayName}
              </a>
            ) : (
              <span
                className="org-owner-broken"
                data-testid={`org-row-owned-by-${row.id}`}
              >
                owned by unknown
              </span>
            )
          ) : null}
        </span>
        <span className="org-row__role">{row.role}</span>
      </span>
      <span className="org-row__meta">
        <span className="org-row__type" data-testid={`org-row-type-${row.id}`}>
          {isAgent ? 'Agent' : 'Human'}
        </span>
        {isAgent && phaseTone ? (
          <StatusPill tone={phaseTone} label={row.phase} />
        ) : null}
        <a
          className="org-row__link"
          href={href}
          data-testid={`org-row-link-${row.id}`}
        >
          {isAgent ? 'Open agent' : 'Open profile'}
        </a>
      </span>
    </li>
  );
}

/**
 * @param {object} props
 * @param {Array} props.rows  Already-filtered rows.
 * @param {Array} [props.allRows]  Full directory (pre-filter) — used for
 *   ownership lookups so chips/links reflect the canonical org, not just
 *   the visible subset.
 * @param {boolean} [props.loading]
 * @param {{from:string, filters: object}} [props.drillContext]
 * @param {(row: object) => void} [props.onOwnerChipClick]
 */
export function OrganizationRoster({
  rows,
  allRows,
  loading,
  drillContext,
  onOwnerChipClick,
}) {
  if (loading) return null;

  const grouped = groupByDept(rows || []);
  const orderedDepts = [...grouped.keys()];
  const sourceForOwnership = allRows || rows || [];

  return (
    <div className="org-grid" data-testid="organization-roster">
      {orderedDepts.map((dept) => {
        const groupRows = grouped.get(dept) || [];
        if (groupRows.length === 0) return null;
        const agents = groupRows.filter((r) => r.type === 'agent').length;
        const humans = groupRows.filter((r) => r.type === 'human').length;
        const health = deptHealth(groupRows);
        // Pick a "featured" treatment for the lone Finance card when any
        // finance agent is Degraded (matches the wireframe's yellow tint).
        const featured = dept === 'finance' && health.tone !== 'ok';
        return (
          <section
            key={dept}
            className={`dept-card${featured ? ' featured' : ''}`}
            data-dept={dept}
            data-testid={`org-dept-group-${dept}`}
          >
            <div className="dh">
              <div className="av" aria-hidden="true">
                {deptInitials(dept)}
              </div>
              <div className="nm">
                <h3 className="dt org-dept-header" style={{ margin: 0 }}>
                  {deptLabel(dept)}
                  <span
                    className="org-dept-count"
                    data-count={groupRows.length}
                    style={{ display: 'none' }}
                  >
                    {groupRows.length}
                  </span>
                </h3>
                <div className="ds">
                  {agents > 0 ? (
                    <span>
                      <b>{agents}</b> agent{agents === 1 ? '' : 's'}
                    </span>
                  ) : null}
                  {humans > 0 ? (
                    <span>
                      <b>{humans}</b> human{humans === 1 ? '' : 's'}
                    </span>
                  ) : null}
                </div>
              </div>
              <span className={`state ${health.tone}`}>{health.label}</span>
            </div>
            <ul className="roles org-rows">
              {groupRows.map((row) => (
                <OrganizationRow
                  key={row.id}
                  row={row}
                  allRows={sourceForOwnership}
                  drillContext={drillContext}
                  onOwnerChipClick={onOwnerChipClick}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
